/* eslint-disable react-hooks/exhaustive-deps */
import LastElementTable from "components/shared/LastElementTable";
import type { LastElementTableColumns } from "components/shared/LastElementTable/type";
import SettingsHeader from "components/shared/SettingsHeader";
import { SmallBody } from "helpers/TypographyComponents";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { NotificationRolePermissions } from "store/notificationStore";
import { getNotificationRoles } from "store/notificationStore";
import { fetchRoles } from "store/permissionsStore";
import { useAppDispatch, useAppSelector } from "store/store";

import RenderRoleCells from "./RenderRoleCells";
import styles from "./style.module.scss";
import type { NotificationType } from "./utils";
import { formatNotificationRolesOptions, scopedNotifications } from "./utils";

const ScopedNotificationSettings = () => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const { notificationRolesPermissions, fetchStatus } = useAppSelector((state) => state.notification);
    const { roles, rolesFetchStatus } = useAppSelector((state) => state.permissions);

    const _fetchStatus = fetchStatus.notificationRolesPermissions;

    const notificationRolesOptions = useMemo(() => {
        if (!notificationRolesPermissions || !roles.length) return undefined;
        return formatNotificationRolesOptions(notificationRolesPermissions, roles);
    }, [notificationRolesPermissions, roles]);

    const scopedNotificationsColumns: LastElementTableColumns[] = [
        {
            id: "notificationType",
            label: "Notifications",
            align: "left",
            format: (value: NotificationType) => (
                <div>
                    <SmallBody>{scopedNotifications[value].title}</SmallBody>
                    <SmallBody color="grey">{scopedNotifications[value].description}</SmallBody>
                </div>
            ),
        },
        {
            id: "shouldNotify",
            label: t("ScopedNotificationSettings.NotifiedRoles"),
            align: "left",
            format: (_, __, currentRow: NotificationRolePermissions) => (
                <RenderRoleCells
                    permissions={currentRow}
                    allRoles={roles}
                    notificationRolesOptions={notificationRolesOptions}
                />
            ),
        },
    ];

    /**
     * Fetch and get initial datas
     */
    useEffect(() => {
        if (_fetchStatus === "idle") {
            dispatch(getNotificationRoles());
        }
        if (rolesFetchStatus === "idle") {
            dispatch(fetchRoles());
        }
    }, [_fetchStatus, rolesFetchStatus]);

    return (
        <div className={styles["scoped-notifications-settings"]}>
            {/** Header */}
            <SettingsHeader
                title={t("Settings.ScopedNotifications")}
                subtitle={t("Settings.ScopedNotificationsSubtitle")}
            />

            {/** Content */}
            <div className={styles["scoped-notifications-settings-content"]}>
                <LastElementTable
                    columns={scopedNotificationsColumns}
                    data={notificationRolesPermissions}
                    allowInfiniteScroll
                    isLoading={_fetchStatus !== "succeeded"}
                    columnsNumber={scopedNotificationsColumns.length}
                />
            </div>
        </div>
    );
};

export default ScopedNotificationSettings;
